import { Link, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Building2,
  Users2,
  ShoppingCart,
  UserCog,
  CircleDollarSign,
  PieChart,
  ChevronDown,
  ChevronUp,
  Menu,
  LogOut,
  Search
} from "lucide-react";

function Layout() {
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));
  
  // Normalized clean role tracker string to handle string parsing edge cases
  const role = user?.role ? String(user.role).toLowerCase() : "";

  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [openPurchase, setOpenPurchase] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.replace("/");
    }
    document.body.style.margin = 0;
    document.body.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace("/");
  };

  const isActive = (path) => location.pathname === path;

  // Clean Typography Role Mapping Engine
  const roleDisplayMap = {
    admin: "System Administrator",
    purchase: "Purchase Executive",
    accounts: "Accounts Team",
    hod: "Head of Department",
    supervisor: "Site Supervisor"
  };

  // High Contrast Production Styling Archetype Layout 
  const t = {
    sidebarBg: darkMode ? "#0f172a" : "#ffffff",
    mainBg: darkMode ? "#0f172a" : "#f8fafc", 
    text: darkMode ? "#f8fafc" : "#0f172a",
    muted: darkMode ? "#94a3b8" : "#64748b",
    border: darkMode ? "#1e293b" : "#e2e8f0",
    activeBg: darkMode ? "#1e293b" : "#f1f5f9",
    activeText: darkMode ? "#3b82f6" : "#2563eb", 
    searchBg: darkMode ? "#1e293b" : "#f1f5f9"
  };

  return (
    <div className="app-shell" style={{ display: "flex", minHeight: "100vh", background: t.mainBg }}>
      
      {/* SIDEBAR MAIN DRAWER ENGINE */}
      <div
        className={`app-sidebar ${mobileOpen ? "is-open" : ""}`}
        style={{
          width: collapsed ? "76px" : "280px",
          background: t.sidebarBg,
          color: t.text,
          transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          borderRight: `1px solid ${t.border}`,
          position: "fixed",
          left: 0,
          top: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          zIndex: 1000,
        }}
      >
        {/* LOGO FRAME SECTOR */}
        <div
          style={{
            height: "70px",
            padding: "0 24px",
            borderBottom: `1px solid ${t.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            boxSizing: "border-box"
          }}
        >
          {!collapsed && (
            <div>
               <img
  src={`${process.env.PUBLIC_URL}/logo.png`}
  alt="Ensileta Logo"
  style={{
    height: "25px",
    width: "auto",
    objectFit: "contain"
  }}
/>
</div>
          )}

          <button
            onClick={() => window.innerWidth < 768 ? setMobileOpen(false) : setCollapsed(!collapsed)}
            style={{
              border: "none",
              background: "transparent",
              color: t.muted,
              cursor: "pointer",
              padding: "6px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Menu size={18} />
          </button>
        </div>

        {/* SIDEBAR SEARCH INTERACTION INPUT */}
        {!collapsed && (
          <div style={{ padding: "16px 16px 8px 16px" }}>
            <div style={{ position: "relative" }}>
              <Search size={15} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: t.muted }} />
              <input
                placeholder="Search modules..."
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px 0 38px",
                  background: t.searchBg,
                  border: "none",
                  outline: "none",
                  borderRadius: "8px",
                  color: t.text,
                  fontSize: "13px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        )}

        {/* WORKSPACE NAVIGATION TREE */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 12px" }}>
          
          <SidebarItem
            collapsed={collapsed}
            text="Dashboard"
            icon={<LayoutDashboard size={18} />}
            active={isActive("/dashboard")}
            to="/dashboard"
            t={t}
          />

          {/* Accounts users do not have access to the Sites / Projects module. */}
          {role !== "accounts" && (
            <SidebarItem
              collapsed={collapsed}
              text="Sites"
              icon={<Building2 size={18} />}
              active={isActive("/sites")}
              to="/sites"
              t={t}
            />
          )}

          <SidebarItem
            collapsed={collapsed}
            text="Vendors"
            icon={<Users2 size={18} />}
            active={isActive("/vendors")}
            to="/vendors"
            t={t}
          />

         {/* PURCHASE MENU */}
        {(role === "admin" || role === "purchase" || role === "accounts" || role === "manager") && (
             <div>
            <div
              onClick={() => setOpenPurchase(!openPurchase)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "space-between",
                padding: "10px 12px",
                marginBottom: "4px",
                borderRadius: "6px",
                cursor: "pointer",
                color: t.muted,
                fontSize: "13.5px",
                fontWeight: "600",
                transition: "background 0.15s ease"
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.activeBg}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <ShoppingCart size={18} color={openPurchase && !collapsed ? t.activeText : t.muted} />
                {!collapsed && <span style={{ color: t.text }}>Purchase</span>}
              </div>

              {!collapsed && (
                <span>
                  {openPurchase ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
              )}
            </div>

            {openPurchase && !collapsed && (
              <div style={{ paddingLeft: "16px", borderLeft: `1px solid ${t.border}`, marginLeft: "20px", marginTop: "4px", marginBottom: "8px" }}>
                
                {role === "manager" ? <SubMenu text="PO List" to="/po-list" active={isActive("/po-list")} t={t} /> : <>
                {/* Purchase and Accounts have the same procurement workspace access. */}
               {(role === "admin" || role === "purchase" || role === "accounts") && (
  <SubMenu
    text={['purchase', 'accounts'].includes(role) ? "Material Master (Read Only)" : "Material Master"}
    to="/material-master"
    active={isActive("/material-master")}
    t={t}
  />
)}

                {/* Show labeled read-only string to the purchase module segment */}
                <SubMenu
                  text={['purchase', 'accounts'].includes(role) ? "Price List (Read-Only)" : "Price List"}
                  to="/price-list"
                  active={isActive("/price-list")}
                  t={t}
                />

                <SubMenu
                  text="BOQ Workspace"
                  to="/boq-workspace"
                  active={isActive("/boq-workspace")}
                  t={t}
                />
                <SubMenu text="Create RFQ" to="/rfq" active={isActive("/rfq")} t={t} />
                <SubMenu text="RFQ List"   to="/rfq-list" active={isActive("/rfq-list")} t={t} />
                <SubMenu text="Comparison" to="/comparison" active={isActive("/comparison")} t={t} />
                <SubMenu text="PO" to="/purchase-order" active={isActive("/purchase-order")} t={t} />
                <SubMenu text="PO List" to="/po-list" active={isActive("/po-list")} t={t} />
                </>}
              </div>
            )}
          </div>
          )}

          {/* ADMIN CONSOLE BLOCKS */}
          {role === "admin" && (
            <>
              {!collapsed && (
                <div style={{ marginTop: "24px", marginBottom: "8px", fontSize: "11px", fontWeight: "700", color: t.muted, paddingLeft: "12px", letterSpacing: "0.05em" }}>
                  ADMIN MANAGEMENT
                </div>
                
              )}
              <SidebarItem
  collapsed={collapsed}
  text="User Management"
  icon={<UserCog size={18} />}
  active={isActive("/user-management")}
  to="/user-management"
  t={t}
/>
            </>
          )}

          {/* FINANCIAL ACCOUNTS SUITE (Completely locked out from Purchase teams) */}
          {!['purchase', 'accounts'].includes(role) && (
            <>
              {!collapsed && (
                <div style={{ marginTop: "24px", marginBottom: "8px", fontSize: "11px", fontWeight: "700", color: t.muted, paddingLeft: "12px", letterSpacing: "0.05em" }}>
                  FINANCIAL ACCOUNTS
                </div>
              )}

              <SidebarItem
                collapsed={collapsed}
                text="Expenses"
                icon={<CircleDollarSign size={18} />}
                active={isActive("/expenses")}
                to="/expenses"
                t={t}
              />

              <SidebarItem
                collapsed={collapsed}
                text="Site Summary"
                icon={<PieChart size={18} />}
                active={isActive("/site-summary")}
                to="/site-summary"
                t={t}
              />
            </>
          )}
        </div>

        {/* IDENTITY FOOTER RENDER MATRIX */}
        <div style={{ padding: "16px", borderTop: `1px solid ${t.border}`, background: t.sidebarBg }}>
          <div style={{ display: "flex", alignItems: "center", justifyValue: "center", justifyContent: collapsed ? "center" : "space-between" }}>
            {!collapsed && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  background: role === "admin" ? "#2563eb" : "#10b981", 
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "700",
                }}>
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: t.text }}>
                    {user?.name}
                  </div>
                  <div style={{ fontSize: "11px", color: t.muted, fontWeight: "500" }}>
                    {roleDisplayMap[role] || role}
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleLogout}
              style={{ border: "none", background: "transparent", color: "#ef4444", cursor: "pointer", padding: "6px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center" }}
              title="Secure Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      <button className={`app-drawer-backdrop ${mobileOpen ? "is-open" : ""}`} aria-label="Close navigation" onClick={() => setMobileOpen(false)} />

      {/* CORE FRAME CONTAINER CONTROLLER */}
      <div className="app-main" style={{
        flex: 1,
        marginLeft: collapsed ? "76px" : "280px",
        transition: "margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        minWidth: 0
      }}>
        {/* HEADER TOPBAR DESCRIPTOR CONTROL PANEL */}
        <div className="app-topbar" style={{
          height: "80px",
          background: t.sidebarBg,
          borderBottom: `1px solid ${t.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 40px",
          position: "sticky",
          top: 0,
          zIndex: 999,
          boxSizing: "border-box"
        }}>
         <div className="app-topbar-title">
          <button className="app-mobile-menu" aria-label="Open navigation" onClick={() => setMobileOpen(true)}><Menu size={20} /></button>
 <div style={{ fontSize: "18px", fontWeight: "700", color: t.text, letterSpacing: "-0.01em" }}>
              Welcome Back Team !
            </div>
            <div style={{ fontSize: "12px", color: t.muted, marginTop: "2px" }}>
              Therefore encourage one another and build each other up, just as in fact you are doing
            </div>
</div>

          {/* SYSTEM INTERACTIVE SETTINGS SWITCHES */}
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <label style={{ position: "relative", width: "42px", height: "22px", display: "inline-block" }}>
              <input type="checkbox" checked={darkMode} onChange={() => setDarkMode(!darkMode)} style={{ display: "none" }} />
              <span style={{ position: "absolute", cursor: "pointer", inset: 0, background: darkMode ? "#22c55e" : "#cbd5e1", borderRadius: "50px", transition: ".2s" }}></span>
              <span style={{ position: "absolute", height: "16px", width: "16px", left: darkMode ? "22px" : "3px", top: "3px", background: "#ffffff", borderRadius: "50%", transition: ".2s", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }}></span>
            </label>
          </div>
        </div>

        {/* CONTAINER VIEWPORTS PORT */}
        {/* Passing down dark mode, text token configs, and role contexts dynamically */}
        <div className="app-content" style={{ padding: "0px", background: t.mainBg, color: t.text, flex: 1 }}>
          <Outlet context={{ darkMode, t, role, user }} />
        </div>
      </div>
    </div>
  );
}

/* NAVIGATION LINK BLOCK LIFTER ITEM */
function SidebarItem({ collapsed, text, icon, active, to, t }) {
  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: "12px",
          padding: "12px 14px",
          borderRadius: "8px",
          marginBottom: "6px",
          cursor: "pointer",
          background: active ? t.activeBg : "transparent",
          color: active ? t.activeText : t.muted,
          fontSize: "14px",
          fontWeight: active ? "600" : "500",
          transition: "all 0.15s ease",
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = t.activeBg; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
      >
        <span style={{ display: "flex", alignItems: "center", color: active ? t.activeText : t.muted }}>
          {icon}
        </span>
        {!collapsed && <span style={{ color: active ? t.activeText : t.text }}>{text}</span>}
      </div>
    </Link>
  );
}

/* COLLAPSIBLE TRACKING SUB-MENU SUB MODULE COMPONENTS */
function SubMenu({ text, to, active, t }) {
  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <div
        style={{
          padding: "10px 14px",
          borderRadius: "8px",
          marginBottom: "4px",
          background: active ? t.activeBg : "transparent",
          color: active ? t.activeText : t.muted,
          fontSize: "13px",
          fontWeight: active ? "600" : "500",
          transition: "all 0.15s ease",
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = t.activeBg; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
      >
        {text}
      </div>
    </Link>
  );
}

export default Layout;
